import { getCDNURL } from '@pickleballinc/lib/get-cdn-url';
import { cn } from '@pickleballinc/ui/lib/utils';

const spinKeyframes = `
@keyframes spin {
	from {
		transform: rotate(0deg);
	}
	to {
		transform: rotate(360deg);
	}
}
`;

type LoaderSize = 'sm' | 'md' | 'lg';

interface LoaderProps {
	moduleFor?: string;
	backgroundColor?: string;
	logo?: string;
	label?: string;
	textSize?: string;
	isComponent?: boolean;
	containerClassName?: string;
	/** Spinner dimensions. Defaults to `md` when `isComponent`, otherwise `lg` — preserving prior behavior. */
	size?: LoaderSize;
}

const Loader = ({
	moduleFor = 'Pickleball',
	backgroundColor,
	logo,
	label = 'Loading...',
	isComponent = false,
	textSize = 'text-lg',
	containerClassName = '',
	size
}: LoaderProps) => {
	const computedBackgroundColor = backgroundColor || (moduleFor === 'Tournaments' ? 'bg-blue-800/90' : 'bg-violet-600/90');
	const computedLogo =
		logo || `https://images.pickleball.com${moduleFor === 'Tournaments' ? '/global/legacy/pt-logo-icon-white.svg' : '/global/PB.svg'}`;

	const effectiveSize: LoaderSize = size ?? (isComponent ? 'md' : 'lg');

	const sizeMap: Record<LoaderSize, { spinner: string; bar: string; translate: string; logo: number }> = {
		sm: { spinner: 'w-10 h-10', bar: 'w-[2px] h-2', translate: '-15px', logo: moduleFor === 'Tournaments' ? 16 : 12 },
		md: { spinner: 'w-[80px] h-[80px]', bar: 'w-[3px] h-4', translate: '-30px', logo: moduleFor === 'Tournaments' ? 30 : 20 },
		lg: { spinner: 'w-[120px] h-[120px]', bar: 'w-1 h-6', translate: '-40px', logo: moduleFor === 'Tournaments' ? 40 : 30 }
	};

	const { spinner: spinnerSize, bar: barSize, translate: translateDistance, logo: logoSize } = sizeMap[effectiveSize];

	return (
		<>
			<style>{spinKeyframes}</style>
			<div
				className={cn(
					'inset-0 flex items-center justify-center backdrop-blur-sm',
					isComponent ? 'relative rounded-md p-2' : 'fixed z-50',
					computedBackgroundColor,
					containerClassName
				)}
			>
				<div className={cn('flex flex-col items-center', isComponent && 'gap-2')}>
					<div className="relative">
						<div className={cn('relative flex items-center justify-center', spinnerSize)}>
							<div
								className={cn('absolute flex items-center justify-center', spinnerSize)}
								style={{ animation: 'spin 4s linear infinite' }}
							>
								{Array.from({ length: 8 }).map((_, i) => (
									<div
										key={i}
										className={cn('absolute bg-white rounded-full', barSize)}
										style={{ transform: `rotate(${i * 45}deg) translateY(${translateDistance})` }}
									/>
								))}
							</div>
							<img src={getCDNURL(computedLogo, logoSize, logoSize)} alt="Loading" width={logoSize} height={logoSize} />
						</div>
					</div>
					{label ? <p className={`font-semibold text-white ${textSize}`}>{label}</p> : null}
				</div>
			</div>
		</>
	);
};

export default Loader;
